import { Callout } from '../ui/callout'
import cx from 'classnames'
import { format } from 'date-fns'
import React, { useEffect, useMemo, useState } from 'react'
import { Subscription } from '../../types/subscription'
import { axiosGet } from '../../util/api'
import { cents, money } from '../../util/formatters'
import { Marketplace, Marketplaces } from '../../util/marketplaces'
import { Button } from '../ui/button'

interface BillingInfoCalloutProps {
    subscription: Subscription
    onCancel: any
    onCancelNow: any
    onResume: any
    btnLoading: any
    hasSalesBasedBillingPlan: boolean
}

export const BillingInfoCallout = ({
    subscription,
    onCancel,
    onResume,
    onCancelNow,
    btnLoading,
    hasSalesBasedBillingPlan,
}: BillingInfoCalloutProps) => {
    const [loading, setLoading] = useState<boolean>(false)
    const [upcomingInvoice, setUpcomingInvoice] = useState<Invoice | null>(null)

    useEffect(() => {
        if (subscription.subscription_id && subscription.active()) {
            setLoading(true)
            axiosGet('billing/invoice/upcoming/' + subscription.subscription_id)
                .then((res) => {
                    setUpcomingInvoice(res.data)
                })
                .finally(() => {
                    setLoading(false)
                })
        }
    }, [])

    const canceled = subscription.hasCanceled()
    const hasAccountCredits = useMemo(
        () => !!upcomingInvoice?.starting_balance,
        [upcomingInvoice?.starting_balance],
    )
    return (
        <>
            {hasAccountCredits && (
                <Callout
                    className={'mb-3'}
                    icon="dollar"
                    title={'Account Credits'}
                    intent={'primary'}
                >
                    {`You currently have ${cents(
                        Math.abs(upcomingInvoice?.starting_balance ?? 0),
                        Marketplaces[Marketplace.UnitedStates],
                    )} in account credits`}
                </Callout>
            )}
            <Callout
                icon={!canceled ? 'tick' : 'dollar'}
                title={'Billing'}
                intent={!canceled ? 'success' : undefined}
            >
                <SubscriptionStatus
                    loading={loading}
                    subscription={subscription}
                    hasSalesBasedBillingPlan={hasSalesBasedBillingPlan}
                />
                <div className={cx({ 'bp4-skeleton': loading })}>
                    {!canceled && subscription.isTrialing() && (
                        <p className="mb-0 pb-0">
                            Your trial is set to expire on {fmtDate(subscription?.trial_end)}
                        </p>
                    )}
                    {hasAccountCredits && (
                        <p className="font-bold">
                            For the following transaction, Merch Jar will attempt to use your
                            account credits first.
                        </p>
                    )}
                    {!canceled && !subscription.isTrialing() && (
                        <p className="mb-0 pb-0">
                            Your subscription is scheduled to renew on{' '}
                            {fmtDate(upcomingInvoice?.period_end)}
                        </p>
                    )}
                    {canceled && (
                        <p className="mb-0 pb-0">
                            Your subscription is scheduled to end on{' '}
                            {fmtDate(upcomingInvoice?.period_end)}:
                        </p>
                    )}
                </div>
                <div className={cx('text-center mt-3', { 'bp4-skeleton': loading })}>
                    {!canceled && (
                        <Button loading={btnLoading} onClick={onCancel}>
                            Cancel Subscription
                        </Button>
                    )}
                    {canceled && (
                        <div>
                            <Button
                                className="mr-4"
                                loading={btnLoading}
                                intent="success"
                                onClick={onResume}
                                text="Resume Subscription"
                            />
                            <Button loading={btnLoading} onClick={onCancelNow} text="Cancel Now" />
                        </div>
                    )}
                </div>
            </Callout>
        </>
    )
}

interface SubscriptionStatusProps {
    loading: boolean
    subscription: Subscription
    hasSalesBasedBillingPlan: boolean
}

const SubscriptionStatus = ({
    loading,
    subscription,
    hasSalesBasedBillingPlan,
}: SubscriptionStatusProps) => {
    const canceled = subscription.hasCanceled()

    return (
        <p className={cx({ 'bp4-skeleton': loading })}>
            {!canceled && (
                <span>
                    Your subscription is currently <b>{subscription.getStatus()}</b>.
                </span>
            )}
            {canceled && !hasSalesBasedBillingPlan && (
                <span>
                    Your subscription is currently active, but it is set to cancel at the end of the
                    billing period. Ad spend costs will still accrue during this time. Clicking the
                    Cancel Now button will immediately stop your subscription and prevent any
                    additional ad spend accrual.
                </span>
            )}
            {canceled && hasSalesBasedBillingPlan && (
                <span>
                    Your subscription is currently active, but it is set to cancel at the end of the
                    billing period. Overages will still accrue during this time. Clicking the Cancel
                    Now button will immediately stop your subscription and prevent any additional
                    overages from accruing.
                </span>
            )}
        </p>
    )
}

const fmtDate = (unixTimestamp: number | undefined): string | null => {
    if (unixTimestamp == undefined) {
        return null
    }
    return format(new Date(unixTimestamp * 1000), 'LLLL do')
}

const getLines = (invoice: Invoice, hasAccountCredits = false) => {
    let base: InvoiceData | null = null
    const adSpend: InvoiceData[] = []
    const discount: InvoiceData[] = []

    if (!invoice?.lines?.data) {
        return null
    }

    invoice.lines.data.forEach((line) => {
        if (line.amount == 0) {
            return
        } else if (line.amount < 0) {
            discount.push(line)
            return
        }

        if (line.quantity == 0) {
            base = line
        } else {
            adSpend.push(line)
        }
    })

    let adSpendQuantity = 0
    let adSpendAmount = 0
    adSpend.forEach((line) => {
        adSpendQuantity += line.quantity
        adSpendAmount += line.amount
    })

    const out = []

    if (base) {
        // @ts-ignore
        out.push(
            <li key="base">
                {cents(base.amount, Marketplaces[Marketplace.UnitedStates])} for {getType(base)}
            </li>,
        )
    }
    out.push(
        <li key="adSpend">
            {cents(adSpendAmount, Marketplaces[Marketplace.UnitedStates])} in accrued ad spend
        </li>,
    )

    discount.forEach((line) => {
        out.push(
            <li key={line.id}>
                {cents(line.amount, Marketplaces[Marketplace.UnitedStates])} for {getType(line)}
            </li>,
        )
    })

    if (hasAccountCredits) {
        // If they had some account credit
        if ((invoice?.starting_balance ?? 0) < 0) {
            out.push(
                <li key="total">
                    {cents(
                        (invoice?.starting_balance ?? 0) - (invoice?.ending_balance ?? 0),
                        Marketplaces[Marketplace.UnitedStates],
                    )}
                    &nbsp;in account credits
                </li>,
            )
        }

        out.push(
            <li key="total">
                <b>{cents(invoice?.amount_due, Marketplaces[Marketplace.UnitedStates])} total</b>
            </li>,
        )
        out.push(
            <p style={{ borderTop: '1px solid black' }} className={'pt-2 mt-2'} key="accountCredit">
                {cents(
                    Math.abs(invoice?.ending_balance ?? 0),
                    Marketplaces[Marketplace.UnitedStates],
                )}{' '}
                account credits will remain
            </p>,
        )
    } else {
        out.push(
            <li key="total">
                <b>{cents(invoice?.amount_due, Marketplaces[Marketplace.UnitedStates])} total</b>
            </li>,
        )
    }

    return out
}

const getType = (line: InvoiceData) => {
    // @ts-ignore
    if (line?.metadata?.type === 'prepaid') {
        // @ts-ignore
        return `${money(line?.metadata?.quantity * 100)} in prepaid ad spend`
    }

    if (line.quantity == 0) {
        return 'base plan'
    } else {
        return `${money(line.quantity * 100)} in accrued ad spend`
    }
}

type Invoice = {
    amount_due: number
    amount_remaining: number
    billing_reason: string
    period_end: number
    lines: Lines
    paid: boolean
    total: number
    subtotal: number
    starting_balance?: number
    ending_balance?: number
}

type Lines = {
    object: string
    data: Array<InvoiceData>
}

type InvoiceData = {
    id: string
    amount: number
    description: string
    period: Array<string>
    plan: PlanType
    price: Price
    quantity: number
}

type PlanType = {
    id: string
    nickname: string
    usage_type: string
}

type Price = {
    id: string
    nickname: string
    unit_amount: number
}
